import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useTasks } from "@/hooks/useTasks";
import { Task } from "@/types";
import { Bell, Clock, Check, AlertTriangle, CalendarClock, Paperclip, ChevronRight, Plus } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { format, isToday, isPast, isTomorrow, parseISO, formatDistanceToNow } from "date-fns";
import { ViewEditTaskDialog } from "./ViewEditTaskDialog";
import { toast } from "sonner";
import { getAllActiveUsers } from "@/services/userService";

type UrgencyGroup = "overdue" | "today" | "upcoming";

const URGENCY_CONFIG: Record<UrgencyGroup, { label: string; icon: typeof AlertTriangle; color: string; badgeVariant: string; borderColor: string }> = {
  overdue: {
    label: "Overdue",
    icon: AlertTriangle,
    color: "text-red-600 dark:text-red-400",
    badgeVariant: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
    borderColor: "border-l-red-500",
  },
  today: {
    label: "Due Today",
    icon: Clock,
    color: "text-orange-600 dark:text-orange-400",
    badgeVariant: "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300",
    borderColor: "border-l-orange-500",
  },
  upcoming: {
    label: "Upcoming",
    icon: CalendarClock,
    color: "text-blue-600 dark:text-blue-400",
    badgeVariant: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
    borderColor: "border-l-blue-500",
  },
};

function getUrgencyGroup(task: Task): UrgencyGroup {
  if (!task.dueDate) return "upcoming";
  const dueDate = typeof task.dueDate === 'string' ? parseISO(task.dueDate) : new Date(task.dueDate);
  if (isToday(dueDate)) return "today";
  if (isPast(dueDate)) return "overdue";
  return "upcoming";
}

function getRelativeTime(task: Task): string {
  if (!task.dueDate) return "";
  const dueDate = typeof task.dueDate === 'string' ? parseISO(task.dueDate) : new Date(task.dueDate);
  
  if (isToday(dueDate)) {
    const timeStr = format(dueDate, "h:mm a");
    return `Today at ${timeStr}`;
  }
  if (isTomorrow(dueDate)) {
    const timeStr = format(dueDate, "h:mm a");
    return `Tomorrow at ${timeStr}`;
  }
  if (isPast(dueDate)) {
    return `${formatDistanceToNow(dueDate)} overdue`;
  }
  return `Due ${formatDistanceToNow(dueDate, { addSuffix: true })}`;
}

interface AssociatedTasksProps {
  entityType: 'lead' | 'vendor' | 'banker';
  entityId: string;
  onAddReminder?: () => void;
}

export function AssociatedTasks({ entityType, entityId, onAddReminder }: AssociatedTasksProps) {
  const { tasks, updateTask } = useTasks();
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [confirmCompleteOpen, setConfirmCompleteOpen] = useState(false);
  const [taskToComplete, setTaskToComplete] = useState<string | null>(null);
  const [availableUsers, setAvailableUsers] = useState<any[]>([]);

  useEffect(() => {
    getAllActiveUsers().then(users => setAvailableUsers(users)).catch(() => {});
  }, []);

  const getCreatorFirstName = (task: Task): string => {
    // Use denormalized name if available (new tasks)
    if (task.createdByName) return task.createdByName.split(' ')[0];
    // Fall back to resolving user ID → name (legacy tasks)
    if (!task.createdBy) return '';
    const name = task.createdBy;
    if (name.includes('user_') || (name.length > 20 && !name.includes(' '))) {
      const found = availableUsers.find(u => (u.userId || u.id) === name);
      return found?.name?.split(' ')[0] || '';
    }
    return name.split(' ')[0];
  };

  // Get all tasks for this entity
  const allEntityTasks = tasks.filter(t => {
    if (entityType === 'lead') return t.leadId === entityId;
    if (entityType === 'vendor') return t.connectorId === entityId;
    if (entityType === 'banker') return t.smContactId === entityId;
    return false;
  });

  // Split into pending and completed
  const pendingTasks = allEntityTasks.filter(t => !t.completed).sort((a, b) => {
    // Sort by urgency then date
    const urgencyOrder: Record<UrgencyGroup, number> = { overdue: 0, today: 1, upcoming: 2 };
    const urgencyA = urgencyOrder[getUrgencyGroup(a)];
    const urgencyB = urgencyOrder[getUrgencyGroup(b)];
    if (urgencyA !== urgencyB) return urgencyA - urgencyB;
    
    const dateA = a.dueDate ? new Date(a.dueDate).getTime() : Infinity;
    const dateB = b.dueDate ? new Date(b.dueDate).getTime() : Infinity;
    return dateA - dateB;
  });

  const completedTasks = allEntityTasks.filter(t => t.completed).sort((a, b) => {
    // Sort completed tasks by completion date (most recent first)
    const dateA = a.dueDate ? new Date(a.dueDate).getTime() : 0;
    const dateB = b.dueDate ? new Date(b.dueDate).getTime() : 0;
    return dateB - dateA;
  });

  // Group pending tasks by urgency
  const groupedPending = pendingTasks.reduce<Record<UrgencyGroup, Task[]>>(
    (acc, task) => {
      const group = getUrgencyGroup(task);
      acc[group].push(task);
      return acc;
    },
    { overdue: [], today: [], upcoming: [] }
  );

  const handleCompleteRequest = (e: React.MouseEvent, taskId: string) => {
    e.stopPropagation();
    setTaskToComplete(taskId);
    setConfirmCompleteOpen(true);
  };

  const handleConfirmComplete = async () => {
    if (taskToComplete) {
      await updateTask(taskToComplete, { completed: true });
      toast.success("Reminder completed");
    }
    setConfirmCompleteOpen(false);
    setTaskToComplete(null);
  };

  const handleTaskUpdate = async (updatedTask: Task) => {
    await updateTask(updatedTask.id, updatedTask);
    setSelectedTask(null);
  };

  const renderTaskCard = (task: Task, index: number) => {
    const urgency = getUrgencyGroup(task);
    const config = URGENCY_CONFIG[urgency];
    const relativeTime = getRelativeTime(task);

    return (
      <motion.div
        key={task.id}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.03 }}
        className={cn(
          "group relative rounded-lg border border-l-4 p-3 cursor-pointer",
          "hover:shadow-md transition-all duration-200",
          config.borderColor,
          urgency === "overdue" && !task.completed
            ? "bg-red-50/50 dark:bg-red-900/10"
            : "bg-card",
          task.completed && "opacity-60"
        )}
        onClick={() => setSelectedTask(task)}
      >
        <div className="flex items-start gap-3">
          {/* Complete checkbox + Attachment indicator */}
          <div className="flex flex-col items-center gap-1">
            {!task.completed && (
              <button
                onClick={(e) => handleCompleteRequest(e, task.id)}
                className={cn(
                  "mt-0.5 flex-shrink-0 h-5 w-5 rounded-full border-2 flex items-center justify-center",
                  "hover:bg-success/20 hover:border-success transition-colors",
                  urgency === 'overdue' ? 'border-red-400' : urgency === 'today' ? 'border-orange-400' : 'border-muted-foreground/40'
                )}
              >
                <Check className="h-3 w-3 opacity-0 group-hover:opacity-50 transition-opacity" />
              </button>
            )}
            {task.completed && (
              <div className="mt-0.5 flex-shrink-0 h-5 w-5 rounded-full bg-success/20 border-2 border-success flex items-center justify-center">
                <Check className="h-3 w-3 text-success" />
              </div>
            )}
            {/* Attachment count */}
            {task.attachments && task.attachments.length > 0 && (
              <div className="flex items-center gap-0.5 text-[10px] text-muted-foreground">
                <Paperclip className="h-3 w-3" />
                <span className="font-medium">{task.attachments.length}</span>
              </div>
            )}
          </div>
          
          {/* Content */}
          <div className="flex-1 min-w-0 space-y-1">
            {/* Description */}
            <p className={cn(
              "text-sm font-medium leading-snug whitespace-pre-wrap break-words",
              task.completed ? "line-through text-muted-foreground" : "text-foreground"
            )}>
              {task.description || task.title}
            </p>
            
            {/* Time */}
            <div className="flex flex-wrap items-center gap-1.5">
              <span className={cn("text-[11px] font-medium", task.completed ? "text-muted-foreground" : config.color)}>
                {relativeTime}{getCreatorFirstName(task) ? ` · ${getCreatorFirstName(task)}` : ''}
              </span>
              {task.completed && (
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-5 font-normal bg-success/10 text-success">
                  Completed
                </Badge>
              )}
            </div>
          </div>
          
          {/* Arrow */}
          <ChevronRight className="h-4 w-4 text-muted-foreground/40 flex-shrink-0 mt-0.5 group-hover:text-muted-foreground transition-colors" />
        </div>
      </motion.div>
    );
  };

  const renderGroup = (group: UrgencyGroup, tasks: Task[]) => {
    const groupTasks = tasks.filter(t => getUrgencyGroup(t) === group);
    if (groupTasks.length === 0) return null;
    const config = URGENCY_CONFIG[group];
    const Icon = config.icon;

    const headerBg =
      group === "overdue"
        ? "bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800/30"
        : group === "today"
        ? "bg-orange-50 dark:bg-orange-900/20 border border-orange-100 dark:border-orange-800/30"
        : "bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/30";

    return (
      <div key={group} className="space-y-1.5">
        <div className={cn("flex items-center gap-1.5 px-2 py-1.5 rounded-md", headerBg)}>
          <Icon className={cn("h-3.5 w-3.5 flex-shrink-0", config.color)} />
          <span className={cn("text-xs font-semibold uppercase tracking-wider", config.color)}>
            {config.label}
          </span>
          <Badge className={cn("text-[10px] px-1.5 py-0 h-4 ml-auto", config.badgeVariant)}>
            {groupTasks.length}
          </Badge>
        </div>
        <div className="space-y-1.5">
          {groupTasks.map((task, i) => renderTaskCard(task, i))}
        </div>
      </div>
    );
  };

  const overdueCount = groupedPending.overdue.length;

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Bell className="h-4 w-4" />
              Associated Reminders
            </CardTitle>
            <div className="flex items-center gap-2">
              {onAddReminder && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 px-2.5 text-xs text-green-700 border-green-300 hover:bg-green-50"
                  onClick={onAddReminder}
                >
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  Add
                </Button>
              )}
              {overdueCount > 0 && (
                <Badge className="bg-red-500 text-white whitespace-nowrap dark:bg-red-600">
                  {overdueCount} Overdue
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="pending" className="w-full">
            <TabsList className="w-full grid grid-cols-2">
              <TabsTrigger value="pending">
                Pending ({pendingTasks.length})
              </TabsTrigger>
              <TabsTrigger value="completed">
                Completed ({completedTasks.length})
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="pending" className="mt-4 space-y-3">
              {pendingTasks.length > 0 ? (
                <AnimatePresence mode="popLayout">
                  {(Object.keys(URGENCY_CONFIG) as UrgencyGroup[]).map(group => 
                    renderGroup(group, pendingTasks)
                  )}
                </AnimatePresence>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No pending reminders for this {entityType === 'lead' ? 'customer' : entityType === 'vendor' ? 'channel partner' : 'banker'}
                </p>
              )}
            </TabsContent>
            
            <TabsContent value="completed" className="mt-4 space-y-1.5">
              {completedTasks.length > 0 ? (
                <AnimatePresence mode="popLayout">
                  {completedTasks.map((task, i) => renderTaskCard(task, i))}
                </AnimatePresence>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No completed reminders for this {entityType === 'lead' ? 'customer' : entityType === 'vendor' ? 'channel partner' : 'banker'}
                </p>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* View/Edit Task Dialog */}
      {selectedTask && (
        <ViewEditTaskDialog
          open={!!selectedTask}
          onOpenChange={(open) => !open && setSelectedTask(null)}
          task={selectedTask}
          onSave={handleTaskUpdate}
        />
      )}

      {/* Confirm Complete Dialog */}
      <AlertDialog open={confirmCompleteOpen} onOpenChange={setConfirmCompleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark as Completed?</AlertDialogTitle>
            <AlertDialogDescription>
              This reminder will be marked as complete.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmComplete} className="bg-success hover:bg-success/90">
              Complete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
